import * as THREE from "three";
import { trafficZ, walkerX, type HillCourse, type HillWalker } from "../hill-course";
import { crossingOf, walkerSpot, type WalkerSpot } from "../hill-walk";
import type { Skater } from "../skater";
import { mergeStatic } from "./merge";
import { buildRider, disposeRider, type SkaterLook, type SkaterRig } from "./skater-parts";
import { WalkPose } from "./walk-pose";

// The people on Bomb the Hill's crosswalks (#196), drawn: built from the
// rider's own parts in other outfits (so there's nothing new to model), and
// walked with the rider's walk cycle (`WalkPose`). Only the people near the
// rider are drawn, from a pool kept per outfit, so a long road doesn't build
// a crowd. Cartoon reactions, all render-side: someone you whip past close
// (or about to be clipped by the traffic) jumps clear, arms up, and someone
// you run into falls flat over and stays down until the next run. Off the
// road they walk the pavement or the footpath to their crossing's
// shopfront and go in (#202, `hill-walk.ts`'s `walkerSpot`).

/** The outfits people wear: `HillWalker.tint` picks one. */
export const PEDESTRIAN_LOOKS: readonly SkaterLook[] = [
  look("#f2c29b", { style: "cap", color: "#3b2a1d", hat: "#e63946" }, { style: "tee", color: "#f1faee" }, { style: "pants", color: "#264653" }, "#22232b"),
  look("#8d5524", { style: "mohawk", color: "#ffb703" }, { style: "tank", color: "#6d597a" }, { style: "cutoffs", color: "#5a7fc4" }, "#f7f7f2"),
  look("#c68642", { style: "cowboy-hat", color: "#2b1d14", hat: "#a0522d" }, { style: "western", color: "#2a9d8f", trim: "#f1faee" }, { style: "pants", color: "#3d405b", buckle: "#ffd166" }, "#5c3a21", "boots"),
  look("#ffdbac", { style: "cap", color: "#e9c46a", hat: "#457b9d" }, { style: "tank", color: "#f4a261" }, { style: "cutoffs", color: "#8d99ae" }, "#e63946"),
  look("#e0ac69", { style: "mohawk", color: "#2a9d8f" }, { style: "tee", color: "#22232b" }, { style: "pants", color: "#6c757d" }, "#f7f7f2", "moon"),
  look("#5c3a21", { style: "cap", color: "#111111", hat: "#ffb703" }, { style: "western", color: "#e76f51", trim: "#264653" }, { style: "cutoffs", color: "#2b2d42" }, "#f1faee"),
];

function look(skin: string, hair: SkaterLook["hair"], top: SkaterLook["top"], bottoms: SkaterLook["bottoms"], shoes: string, shoeStyle: SkaterLook["shoeStyle"] = "skate"): SkaterLook {
  return { skin, hair, top, bottoms, tattoos: [], shoes, shoeStyle, deck: top.color, wheels: "#ffffff" };
}

/** Who's drawn: people this far behind the rider to this far ahead (m). */
export const PEOPLE_BEHIND = 12;
export const PEOPLE_AHEAD = 70;
/** And never more than this many at once (each is a few dozen draw calls). */
export const MAX_PEOPLE = 10;
/** A jump clear: how long (s) and how high (m). */
const HOP_TIME = 0.55;
const HOP_HEIGHT = 0.55;
/** How close (m) the rider, or a vehicle's end, comes before someone jumps clear. */
const HOP_NEAR = 1.9;
/** How far a stride carries a walker (m), for the walk cycle. */
const STRIDE = 0.65;
/** How long (s) falling flat takes. */
const FALL_TIME = 0.4;
/** The rider's hips over the feet, standing (as `SkaterModel`'s). */
const STAND_HIP = 0.7;

/** Which outfit a walker wears. */
export function lookOf(w: HillWalker): number {
  return Math.floor(w.tint * PEDESTRIAN_LOOKS.length) % PEDESTRIAN_LOOKS.length;
}

/** One person's model and how it's moving. */
interface Body {
  rig: SkaterRig;
  walk: WalkPose;
  look: number;
  steps: number;
  lastX: number;
  lastZ: number;
  /** Heading: the way they last walked (radians about y, 0 down the hill). */
  facing: number;
  /** Seconds into a jump clear (Infinity when not jumping). */
  hop: number;
  /** Seconds since they were knocked flat, or -1 standing. */
  fall: number;
  /** The arms' rest pose at the shoulder, front and back, swung from each frame. */
  rest: [THREE.Euler, THREE.Euler];
}

/** What the people react to: where the rider is and whether they just ran into someone. */
export interface PeopleRider {
  x: number;
  z: number;
  /** The run ended on someone crossing (#196). */
  hitPerson: boolean;
}

/** The skater as the people see them. */
export function peopleRider(sk: Skater): PeopleRider {
  return { x: sk.x, z: sk.z, hitPerson: sk.hill?.hit === "pedestrian" };
}

/** A walk's foot state for `WalkPose`: only the stride and pace matter. */
function footState(steps: number, speed: number) {
  return { foot: { speed, vy: 0, airborne: false, steps, runOut: 0, ledge: null } } as unknown as Skater;
}

export class PedestrianMeshes {
  /** Not sheared with the road: each person stands at the hill's height where they are. */
  readonly root = new THREE.Group();
  private bodies = new Map<HillWalker, Body>();
  private spare: Body[][] = PEDESTRIAN_LOOKS.map(() => []);
  private fallen: HillWalker | null = null;
  private lastClock = Number.NaN;

  constructor(private grade: number) {
    this.root.name = "hill-people";
  }

  /** Poses the people near the rider at sim time `clock`. */
  update(course: HillCourse, clock: number, rider: PeopleRider | null): void {
    const dt = Number.isFinite(this.lastClock) ? Math.min(0.1, Math.max(0, clock - this.lastClock)) : 0;
    this.lastClock = clock;
    const z = rider?.z ?? course.z0;
    const near = new Set<HillWalker>();
    for (const w of course.walkers) {
      if (w.z < z - PEOPLE_BEHIND) continue;
      if (w.z > z + PEOPLE_AHEAD || near.size >= MAX_PEOPLE) break;
      near.add(w);
    }
    for (const [w, b] of this.bodies) {
      if (near.has(w)) continue;
      this.bodies.delete(w);
      b.rig.root.visible = false;
      this.spare[b.look]!.push(b);
    }
    if (this.fallen && !near.has(this.fallen)) this.fallen = null;
    if (rider?.hitPerson && !this.fallen) this.fallen = nearest(course, near, rider, clock);
    if (!rider?.hitPerson) this.fallen = null;
    for (const w of near) {
      const b = this.bodies.get(w) ?? this.take(w, course, clock);
      this.pose(b, w, course, clock, dt, rider);
    }
  }

  private take(w: HillWalker, course: HillCourse, clock: number): Body {
    const i = lookOf(w);
    const b = this.spare[i]!.pop() ?? this.build(i);
    b.rig.root.visible = true;
    const at = spot(course, w, clock);
    b.lastX = at.x;
    b.lastZ = at.z;
    b.hop = Infinity;
    b.fall = -1;
    this.bodies.set(w, b);
    return b;
  }

  private build(i: number): Body {
    const rig = buildRider(PEDESTRIAN_LOOKS[i]!);
    // On foot with nothing: no board, no backpack, nothing in hand.
    const pack = rig.torso.getObjectByName("backpack");
    for (const o of [rig.board, rig.gun, rig.washer.wand, rig.washer.tank, pack]) {
      if (!o) continue;
      o.removeFromParent();
      disposeRider(o);
    }
    batchJoints(rig);
    // Only the body casts a shadow: a head's or a hand's adds draw calls nobody sees at speed.
    const body = new Set<THREE.Object3D>([rig.hips, rig.torso]);
    rig.root.traverse((o) => {
      if (o instanceof THREE.Mesh && !(o.parent?.parent && body.has(o.parent.parent))) o.castShadow = false;
    });
    rig.root.name = "pedestrian";
    this.root.add(rig.root);
    const rest: [THREE.Euler, THREE.Euler] = [rig.armFront.shoulder.rotation.clone(), rig.armBack.shoulder.rotation.clone()];
    return { rig, walk: new WalkPose(rig), look: i, steps: 0, lastX: 0, lastZ: 0, facing: Math.PI / 2, hop: Infinity, fall: -1, rest };
  }

  private pose(b: Body, w: HillWalker, course: HillCourse, clock: number, dt: number, rider: PeopleRider | null): void {
    const at = spot(course, w, clock);
    // Indoors (#202): out of sight until they come back out.
    b.rig.root.visible = !at.inside;
    const { x, z } = at;
    const moved = Math.hypot(x - b.lastX, z - b.lastZ);
    if (moved > 1e-4) b.facing = Math.atan2(x - b.lastX, z - b.lastZ);
    b.lastX = x;
    b.lastZ = z;
    b.steps += moved / STRIDE;
    const speed = dt > 0 ? moved / dt : 0;

    // Jump clear of the rider whipping past, or a vehicle coming through.
    const close = (px: number, pz: number, hw: number, hl: number) => Math.abs(px - x) <= hw + HOP_NEAR && Math.abs(pz - z) <= hl + HOP_NEAR;
    const threatened =
      (rider !== null && close(rider.x, rider.z, 0.3, 0.3)) ||
      course.traffic.some((v) => close(v.x, trafficZ(course, v, clock), v.hw, v.hl));
    if (this.fallen === w) b.fall = b.fall < 0 ? 0 : b.fall + dt;
    else {
      // Back on their feet for the next run.
      b.fall = -1;
      if (threatened && !(b.hop < HOP_TIME)) b.hop = 0;
      else b.hop += dt;
    }

    const { root, yawGroup, tilt } = b.rig;
    const up = b.hop < HOP_TIME ? HOP_HEIGHT * Math.sin((Math.PI * b.hop) / HOP_TIME) : 0;
    root.position.set(x, -this.grade * z + up, z);
    // The rider's walk faces +z: turn to face the way they're walking.
    yawGroup.rotation.y = b.facing;
    // Knocked flat: tipped over down the hill, away from the hit.
    tilt.rotation.x = b.fall >= 0 ? (Math.PI / 2) * Math.min(1, b.fall / FALL_TIME) : 0;
    const walking = b.fall < 0 && !(b.hop < HOP_TIME);
    const state = footState(b.steps, walking ? speed * 2 : 0);
    b.walk.blend = 1;
    b.walk.ease(state, dt);
    b.walk.apply(state, STAND_HIP);
    // Arms swing with the stride; up in the air in a jump or a fall.
    // Forward is -x in the rider's frame: a negative turn about z raises an arm in front.
    const throwUp = b.fall >= 0 || b.hop < HOP_TIME ? -2.6 : 0;
    const swing = b.walk.armSwing(state.foot!);
    const [front, back] = b.rest;
    b.rig.armFront.shoulder.rotation.set(front.x, front.y, front.z + swing + throwUp);
    b.rig.armBack.shoulder.rotation.set(back.x, back.y, back.z - swing + throwUp);
  }

  dispose(): void {
    for (const b of [...this.bodies.values(), ...this.spare.flat()]) disposeRider(b.rig.root);
    this.bodies.clear();
    this.spare = PEDESTRIAN_LOOKS.map(() => []);
  }
}

/** Where to draw someone (#202): on the road where the sim has them, off it on their way to or from the shopfront. */
function spot(course: HillCourse, w: HillWalker, clock: number): WalkerSpot {
  const c = crossingOf(course, w);
  return c ? walkerSpot(course, c, w, clock) : { x: walkerX(course, w, clock), z: w.z, inside: false };
}

/** The person nearest the rider: the one they ran into. */
function nearest(course: HillCourse, near: Set<HillWalker>, rider: PeopleRider, clock: number): HillWalker | null {
  let best: HillWalker | null = null;
  let d = Infinity;
  for (const w of near) {
    const e = Math.hypot(walkerX(course, w, clock) - rider.x, w.z - rider.z);
    if (e < d) {
      d = e;
      best = w;
    }
  }
  return best;
}

/**
 * Batches a pedestrian's meshes joint by joint: everything that moves with
 * one joint (the head, a forearm, a shin...) draws as one mesh per material,
 * so a person is a few dozen draw calls rather than the rider's ~90. The
 * joints the walk moves stay where they are.
 */
export function batchJoints(rig: SkaterRig): void {
  const materials = new Map<string, THREE.Material>();
  const same = (m: THREE.Material): THREE.Material => {
    const key = materialKey(m);
    const had = materials.get(key);
    if (had) return had;
    materials.set(key, m);
    return m;
  };
  const joints = new Set<THREE.Object3D>([
    rig.root,
    rig.tilt,
    rig.yawGroup,
    rig.rider,
    rig.hips,
    rig.torso,
    rig.head,
    ...[rig.armFront, rig.armBack].flatMap((a) => [a.shoulder, a.elbow, a.hand]),
    ...rig.legs.flatMap((l) => [l.hip, l.knee, l.foot]),
  ]);
  rig.root.updateMatrixWorld(true);
  const holdsJoint = (o: THREE.Object3D): boolean => o.children.some((c) => joints.has(c) || holdsJoint(c));
  for (const joint of joints) {
    // The meshes that move with this joint and nothing below it.
    const parts: THREE.Mesh[] = [];
    const walk = (o: THREE.Object3D) => {
      for (const c of o.children) {
        if (joints.has(c)) continue;
        if (c instanceof THREE.Mesh && c.name !== "outline" && !holdsJoint(c) && !Array.isArray(c.material)) parts.push(c);
        else if (!(c instanceof THREE.Mesh)) walk(c);
      }
    };
    walk(joint);
    if (parts.length < 2) continue;
    const toJoint = joint.matrixWorld.clone().invert();
    // Parts built with their own copy of the same material share one, and one ink line, so they batch.
    const ink = parts.flatMap((m) => m.children).find((c): c is THREE.Mesh => c instanceof THREE.Mesh && c.name === "outline")?.material;
    // Parts only merge with ones that have the same vertex attributes.
    const sets = new Map<string, THREE.Group>();
    for (const m of parts) {
      const key = Object.keys(m.geometry.attributes).sort().join();
      const tmp = sets.get(key) ?? new THREE.Group();
      sets.set(key, tmp);
      const copy = new THREE.Mesh(m.geometry, same(m.material as THREE.Material));
      copy.castShadow = m.castShadow;
      copy.receiveShadow = m.receiveShadow;
      copy.renderOrder = m.renderOrder;
      const line = m.children.find((c): c is THREE.Mesh => c instanceof THREE.Mesh && c.name === "outline");
      if (line && ink) {
        const o = new THREE.Mesh(m.geometry, ink);
        o.name = "outline";
        copy.add(o);
      }
      copy.matrixAutoUpdate = false;
      copy.matrix.multiplyMatrices(toJoint, m.matrixWorld);
      tmp.add(copy);
      m.removeFromParent();
    }
    for (const tmp of sets.values()) joint.add(mergeStatic(tmp));
  }
}

/** What makes two materials draw the same: their kind, colours, surface and maps. */
function materialKey(m: THREE.Material): string {
  const s = m as THREE.MeshStandardMaterial;
  return [
    m.type,
    m.name,
    s.color?.getHexString(),
    s.emissive?.getHexString(),
    s.roughness,
    s.metalness,
    s.map?.uuid,
    s.normalMap?.uuid,
    s.roughnessMap?.uuid,
    m.transparent,
    m.opacity,
    m.side,
    m.vertexColors,
    s.flatShading,
    m.customProgramCacheKey(),
    JSON.stringify(m.userData),
  ].join("|");
}
